import { useMemo, useState } from 'react'
import cx from 'classnames'
import { useCalendarItem } from 'screens/Calendar/FullScreenPreview/useCalendarItem'
import { relativeDateWithTime } from 'utils/dates'
import { wordCountOfDoc } from 'utils/wordCountOfDoc'
import Avatar from 'common/Avatar/Avatar'
import { getInitials } from 'utils'
import { useProseInstance } from 'providers/ProseInstanceProvider'
import { useInterval } from 'hooks/UseInterval'

import Styles from './NativeDocInfoContainer.module.scss'

interface Props {
  articleKey: string
  className?: string
}

interface PropertyItem {
  label: string
  value: string
  isOwner?: boolean
  ownerData?: {
    name: string
    avatar_url?: string
  }
}

export const NativeDocInfoContainer = ({ articleKey, className }: Props) => {
  const { data: calendarItemData, isLoading } = useCalendarItem({
    id: articleKey,
    filters: {},
    startDate: new Date().toISOString(),
  })
  const { currentViewRef } = useProseInstance()
  const [wordCount, setWordCount] = useState('0')

  useInterval(() => {
    if (!currentViewRef.current) {
      return
    }
    const newWordCount = wordCountOfDoc(currentViewRef.current.state.doc)
    setWordCount(newWordCount)
  }, 1000)

  const properties = useMemo((): PropertyItem[] => {
    if (!calendarItemData?.almanac_file) return []

    const { almanac_file } = calendarItemData
    const owner = almanac_file.owner?.display_name || 'Unknown'

    return [
      {
        label: 'Owner',
        value: owner,
        isOwner: true,
        ownerData: {
          name: owner,
          avatar_url: almanac_file.owner?.avatar_url || undefined,
        },
      },
      {
        label: 'Created',
        value: relativeDateWithTime(almanac_file.created_at),
      },
      {
        label: 'Updated',
        value: relativeDateWithTime(almanac_file.updated_at), // Using post_at as updated_at
      },
      {
        label: 'Word Count',
        value: wordCount,
      },
    ]
  }, [calendarItemData, wordCount])

  if (isLoading) {
    return (
      <div className={cx(Styles.container, className)}>
        <div className={Styles.loading}>Loading...</div>
      </div>
    )
  }

  return (
    <div className={cx(Styles.container, className)}>
      <ul className={Styles.list}>
        {properties.map((property, index) => (
          <li className={Styles.listItem} key={index}>
            <div className={Styles.propertyItem}>
              <span className={Styles.label}>{property.label}</span>
              <span className={Styles.value}>
                {property.isOwner && property.ownerData ? (
                  <div className={Styles.ownerContainer}>
                    <Avatar
                      url={property.ownerData.avatar_url}
                      label={getInitials(property.ownerData.name)}
                      size={20}
                      className={Styles.avatar}
                    />
                    <span className={Styles.ownerName}>{property.ownerData.name}</span>
                  </div>
                ) : (
                  property.value
                )}
              </span>
            </div>
          </li>
        ))}
      </ul>
    </div>
  )
}
